import React, { useState, useEffect, memo, useRef } from 'react';
import { DEVICES_PAGE_TAB, LOADING_CIRCLE, USER_PREFERENCE_SETS } from 'common/utils/constants';
import Modal from 'components/shared/Modal/Modal';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { updateDevicesPageCreateFilterModal } from 'redux/actions';
import { getFilterSetToSave } from '../FiltersBar/utils';
import { SPECIAL_CHARS_FORMAT } from 'common/utils/constants';
import { logger } from 'common/utils/helpers';

import './CreateFilterSetModal.css';
import { selectUserPreferences } from '../../../redux/selectors';
import { selectUserPreferenceList, createUserPreference } from '../../../redux/UserPreferences';

const CreateFilterSetModal = (props) => {
  const mountedRef = useRef(false);
  const dispatch = useDispatch();
  const isModalOpen = useSelector((state) => state.ui.devicesPage.createFilterSetModal);
  const allFilterSets = useSelector((state) =>
    selectUserPreferenceList(selectUserPreferences(state), USER_PREFERENCE_SETS.FILTER_SET)
  );
  const activeTab = useSelector((state) => state.ui.devicesPage.activeTab);
  const activeFilterBody = useSelector((state) => {
    switch (activeTab) {
      case DEVICES_PAGE_TAB.pivot:
        return state.data.pivotFilterDictionary;
      case DEVICES_PAGE_TAB.echo:
        return state.data.echoFilterDictionary;
      case DEVICES_PAGE_TAB.all:
        return state.data.allDevicesFilterDictionary;
      default:
        return state.data.filterDictionary;
    }
  });

  const [inputValue, setInputValue] = useState('my new filter set');
  const [inputError, setInputError] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    inputOnChange(inputValue);
  });

  const onClose = () => {
    if (isModalOpen) dispatch(updateDevicesPageCreateFilterModal(false));
  };

  const onCreate = () => {
    const translatedFilterSet = getFilterSetToSave(activeFilterBody, activeTab === DEVICES_PAGE_TAB.all);
    const requestBody = {
      name: inputValue,
      default: isDefault,
      category: USER_PREFERENCE_SETS.FILTER_SET,
      state: translatedFilterSet
    };

    // @todo notify action
    dispatch(createUserPreference(requestBody))
      .then(() => {
        props.notify('The new filter set is saved');
      })
      .catch((error) => {
        logger.error(error);
        props.notify('Error while creating the new filter set, please try again later');
      })
      .finally(() => {
        if (mountedRef.current) {
          setActionPending(false);
        }
      });

    onClose();
  };

  /**
   * update input value and potential error messages
   * @param {string} targetString
   */
  const inputOnChange = (targetString) => {
    setInputValue(targetString);

    let errorMessage;

    if (targetString.length > 50) {
      errorMessage = 'Number of characters should NOT be more than 50.';
    } else if (targetString.length === 0) {
      errorMessage = 'Filter set name cannot be empty.';
    } else if (SPECIAL_CHARS_FORMAT.test(targetString)) {
      errorMessage = 'Filter set name should NOT contain special character.';
    } else if (isDuplicatedName(targetString)) {
      errorMessage = 'Filter set name already existed.';
    }
    setInputError(errorMessage);
  };

  const isDuplicatedName = (targetString) => {
    return allFilterSets.some((filter) => filter.name === targetString);
  };

  let overlay = null;
  if (actionPending) {
    overlay = <div className="Global_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
  }

  return (
    <Modal animateX={false} global close={onClose} noPadding height={320} width={780}>
      <div className="CreateFilterSetsModal">
        {overlay}
        <div className="CreateFilterSetsModal_Header">Save filter set as</div>
        <div
          className={classnames({
            CreateFilterSetsModal_Content: true,
            CreateFilterSetsModal_Content_ActionPending: actionPending
          })}
        >
          <div className="CreateFilterSetsModal_Content_Item">
            <div className="CreateFilterSetsModal_Content_Item_Header">
              <div className="CreateFilterSetsModal_Content_Item_Header_Container">
                <div className="CreateFilterSetsModal_Content_Item_Header_Input">
                  <input onChange={(evt) => inputOnChange(evt.target.value)} value={inputValue} type="text" />
                </div>
                <div
                  className="CreateFilterSetsModal_Content_Item_Header_Default"
                  onClick={() => setIsDefault(!isDefault)}
                >
                  {
                    <SvgAF
                      img={isDefault ? IconLoader.bttn_make_default_select : IconLoader.bttn_make_default}
                      hover={isDefault ? IconLoader.bttn_make_default_select : IconLoader.bttn_make_default_hover}
                    />
                  }
                </div>
              </div>
              {inputError && <div className="CreateFilterSetsModal_ErrorMessage">{inputError}</div>}
            </div>
          </div>
          <div className="CreateFilterSetsModal_Bottom">
            <button className="CreateFilterSetsModal_Bottom_CancelButton" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className={classnames({
                CreateFilterSetsModal_Bottom_SaveButton: true,
                CreateFilterSetsModal_Bottom_SaveButton_Disabled: inputError
              })}
              onClick={onCreate}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default memo(CreateFilterSetModal);
